import { checkinAnchor } from "./checkin-anchor";
import { F01_ORIGINALS_ANCHOR, memoryItemAnchor } from "./coach-brief-anchors";

// TES-1206 — the coach's patient workspace as tabs. A-01:
// "Den fortsatta patientarbetsytan delas i separata flikar: Chatt, Incheckningar och Patientbild. Innehållet får inte staplas efter varandra i en enda lång sida."
//
// The URL hash picks the tab, so every link that already lands on this page
// keeps working: the work queue's `?utkast=valkomst#chat` (TES-1189), the
// summary's `#minne-<id>` and `#f01-originalsvar`, the trend's
// `#incheckning-<id>`. A hash that names something above the tabs
// (`#handoff`, `#aktuellt`) leaves the current tab alone. Pure, so the
// mapping runs under `node --test`.

export const COACH_TABS = [
  { id: "chatt", label: "Chatt" },
  { id: "incheckningar", label: "Incheckningar" },
  { id: "patientbild", label: "Patientbild" },
] as const;
export type CoachTab = (typeof COACH_TABS)[number]["id"];

// The admin's read-only support view (TES-1040) has no chat, so it has no
// Chatt tab and opens on Incheckningar.
export function visibleCoachTabs(readOnly: boolean) {
  return COACH_TABS.filter((t) => !(readOnly && t.id === "chatt"));
}

export function defaultCoachTab(readOnly: boolean): CoachTab {
  return readOnly ? "incheckningar" : "chatt";
}

// Ids rendered inside Patientbild that a link may name. Historik is a
// sub-level of it (A-01: "Historik kan vara en undernivå i Patientbild").
export const HISTORY_ANCHOR = "historik";

const MEMORY_PREFIX = memoryItemAnchor("");
const CHECKIN_PREFIX = checkinAnchor("");

function idOf(hash: string): string {
  const raw = hash.replace(/^#/, "");
  try {
    return decodeURIComponent(raw);
  } catch {
    // A malformed escape is still an id; it just matches nothing.
    return raw;
  }
}

/**
 * The tab a hash belongs to, or null when it names nothing inside a tab that
 * this view shows.
 */
export function tabForHash(hash: string, readOnly = false): CoachTab | null {
  const tab = tabOf(idOf(hash));
  return tab && visibleCoachTabs(readOnly).some((t) => t.id === tab)
    ? tab
    : null;
}

function tabOf(id: string): CoachTab | null {
  if (id === "") return null;
  // `#chat` is PatientChatPane's own id and what the queue links to.
  if (id === "chatt" || id === "chat") return "chatt";
  if (id === "incheckningar" || id.startsWith(CHECKIN_PREFIX)) {
    return "incheckningar";
  }
  if (
    id === "patientbild" ||
    id === HISTORY_ANCHOR ||
    id === F01_ORIGINALS_ANCHOR ||
    id.startsWith(MEMORY_PREFIX)
  ) {
    return "patientbild";
  }
  return null;
}

/**
 * The element a hash asks to be scrolled to, once its tab shows. A tab's own
 * name selects it without scrolling: the tab bar is already in view.
 */
export function scrollTargetForHash(hash: string): string | null {
  const id = idOf(hash);
  if (id === "" || COACH_TABS.some((t) => t.id === id)) return null;
  return id;
}
